import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard, FileText, Users, Settings, ChevronLeft, ChevronRight,
  CalendarDays, Type, ScanLine, ClipboardList, SendHorizonal, FolderOpen, CalendarClock
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { capabilitiesFor } from '../../lib/permissions';

const WaffleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="2.5" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="7" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="2.5" cy="7" r="1.75" fill="#888888" />
    <circle cx="7" cy="7" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="7" r="1.75" fill="#888888" />
    <circle cx="2.5" cy="11.5" r="1.75" fill="#888888" />
    <circle cx="7" cy="11.5" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="11.5" r="1.75" fill="#888888" />
  </svg>
);

// NOTE: The "import { capabilitiesFor }" above is redundant with the one below — keep this single copy.

const LUMINA_LOGO_WHITE = "https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/wnwhtsce_lumina_logo_white.png";

const PROVIDER_SECTIONS = [
  { label: 'Overview', items: [
    { name: 'Dashboard', href: '/lumina', icon: LayoutDashboard },
    { name: 'Schedule', href: '/lumina/scheduler', icon: CalendarClock },
  ]},
  { label: 'Clinical Care', items: [
    { name: 'Appointments', href: '/lumina/appointments', icon: CalendarDays },
    { name: 'Patients', href: '/lumina/patients', icon: Users },
    { name: 'Encounter Review', href: '/lumina/documentation', icon: FileText },
  ]},
  { label: 'Intelligence', items: [
    { name: 'AI Insights', href: '/lumina/ai-insights', icon: () => <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg> },
    { name: 'MIPS Dashboard', href: '/lumina/mips', icon: () => <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg> },
  ]},
  { label: 'Insights', items: [
    { name: 'Integrations', href: '/lumina/integrations', icon: () => <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.073a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.34 8.374" /></svg> },
    { name: 'Credits & Usage', href: '/lumina/credits', icon: () => <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" /></svg> },
  ]},
];

const MA_SECTIONS = [
  { label: 'Schedule', items: [
    { name: "Today's Patients", href: '/lumina/ma', icon: LayoutDashboard },
    { name: 'Appointments', href: '/lumina/appointments', icon: CalendarDays },
  ]},
  { label: 'MA Tools', items: [
    { name: 'Pre-Visit Prep', href: '/lumina/ma/prep', icon: ClipboardList },
  ]},
  { label: 'Workflow', items: [
    { name: 'Encounter Review', href: '/lumina/documentation', icon: FileText },
    { name: 'Handoff Notes', href: '/lumina/ma/handoff', icon: SendHorizonal },
  ]},
];

const LuminaSidebar = ({ collapsed, setCollapsed, role }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canTeamAdmin } = capabilitiesFor(user);
  const isMA = role === 'ma';
  // Clone PROVIDER_SECTIONS so we can optionally append admin items without mutating the module const.
  const providerSections = PROVIDER_SECTIONS.map(s => ({ ...s, items: [...s.items] }));
  // NOTE: "Team & Roles" used to live under an Admin sidebar section. As of
  // 2026-04-20 IA consolidation, all team + roles management moved into
  // `/lumina/settings` (Teams + Roles tabs). Legacy `/lumina/team` now
  // redirects to Settings, so no dedicated sidebar link is required.
  void canTeamAdmin;
  const navSections = isMA ? MA_SECTIONS : providerSections;

// Shared scheduler path set — `/lumina/scheduler` is canonical, `/echo/scheduler`
// is a redirect alias. Any sidebar rendered while the user is on either path
// should highlight its Schedule entry, regardless of which app hosts the link.
const SCHEDULER_PATHS = new Set(['/lumina/scheduler', '/echo/scheduler', '/revq/scheduler']);
const isSchedulerItem = (item) =>
  SCHEDULER_PATHS.has(item.href);
const isOnSchedulerPath = (pathname) => SCHEDULER_PATHS.has(pathname);

  const isItemActive = (item) => {
    const path = location.pathname;
    if (isSchedulerItem(item) && isOnSchedulerPath(path)) return true;
    if (path === item.href) return true;
    if (item.href === '/lumina' || item.href === '/lumina/ma') return path === item.href;
    return false;
  };

  return (
    <aside className={cn(
      "fixed left-0 top-14 h-[calc(100vh-3.5rem)] transition-all duration-200 z-40 flex flex-col",
      collapsed ? "w-16" : "w-60",
      isMA ? "bg-[#1a1a1a]" : "bg-white border-r border-slate-200"
    )} style={isMA ? { borderRight: '0.5px solid #2a2a2a' } : {}} data-testid="lumina-sidebar">

      {isMA && !collapsed && (
        <div className="px-5 pt-5 pb-1">
          <img src={LUMINA_LOGO_WHITE} alt="Lumina" className="h-5 w-auto object-contain" style={{ maxWidth: 90 }} data-testid="sidebar-lumina-logo" />
        </div>
      )}

      {/* Waffle — All Apps */}
      <div className="px-3 pt-3 pb-1">
        <Link to="/landing"
          className={cn(
            "flex items-center gap-2.5 px-3 py-2.5 rounded-md transition-all",
            isMA ? "border border-[#2a2a2a] hover:bg-white/[0.04]" : "border border-slate-200 hover:bg-[rgba(107,92,231,0.08)]",
            collapsed && "justify-center px-2"
          )}
          style={{ borderRadius: 6 }}
          data-testid="sidebar-all-apps"
        >
          <WaffleIcon />
          {!collapsed && (
            <div>
              <p style={{ fontSize: 12, fontWeight: 500, color: isMA ? '#cccccc' : '#333333', lineHeight: '14px', margin: 0 }}>All apps</p>
              <p style={{ fontSize: 11, fontWeight: 400, color: '#888888', lineHeight: '13px', margin: 0 }}>Aria One platform</p>
            </div>
          )}
        </Link>
      </div>

      <nav className="flex-1 py-3 px-3 space-y-5 overflow-y-auto">
        {navSections.map((section) => (
          <div key={section.label}>
            {!collapsed && (
              <p className={cn(
                "px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]",
                isMA ? "text-[#555555]" : "text-slate-400"
              )}>{section.label}</p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const active = isItemActive(item);
                const IconComp = item.icon;
                return (
                  <Link key={item.name} to={item.href} data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all",
                      isMA ? (
                        active
                          ? "bg-white/[0.12] text-white font-medium"
                          : "text-[#777777] hover:bg-white/[0.05] hover:text-[#bbbbbb]"
                      ) : (
                        active
                          ? "bg-slate-100 text-slate-900 font-medium"
                          : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                      ),
                      collapsed && "justify-center px-2"
                    )}>
                    <IconComp className={cn("h-4 w-4 flex-shrink-0",
                      isMA ? (active ? "text-white" : "text-[#555555]") : "")} />
                    {!collapsed && <span>{item.name}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className={cn("p-3 space-y-2", isMA ? "border-t border-[#2a2a2a]" : "border-t border-slate-200")}>
        {!isMA && (
          <button onClick={() => {
            const root = document.documentElement;
            const current = root.getAttribute('data-font-size') || 'small';
            const next = current === 'small' ? 'medium' : current === 'medium' ? 'large' : 'small';
            root.setAttribute('data-font-size', next);
            root.classList.remove('font-size-small', 'font-size-medium', 'font-size-large');
            root.classList.add(`font-size-${next}`);
            localStorage.setItem('lumina-font-size', next);
          }} className={cn("flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-slate-800 hover:bg-slate-50 transition-all w-full", collapsed && "justify-center px-2")} data-testid="font-size-btn" title="Toggle font size">
            <Type className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="text-xs">Aa</span>}
          </button>
        )}

        {!isMA && (
          <Link to="/lumina/my-documents" data-testid="nav-my-documents"
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all",
              isMA
                ? (location.pathname === '/lumina/my-documents' ? "bg-white/[0.12] text-white" : "text-[#777777] hover:bg-white/[0.05] hover:text-[#bbbbbb]")
                : (location.pathname === '/lumina/my-documents' ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"),
              collapsed && "justify-center px-2"
            )}>
            <FolderOpen className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>My Documents</span>}
          </Link>
        )}

        <Link to="/lumina/settings" data-testid="nav-settings"
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all",
            isMA
              ? (location.pathname === '/lumina/settings' ? "bg-white/[0.12] text-white" : "text-[#777777] hover:bg-white/[0.05] hover:text-[#bbbbbb]")
              : (location.pathname === '/lumina/settings' ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"),
            collapsed && "justify-center px-2"
          )}>
          <Settings className={cn("h-4 w-4 flex-shrink-0", isMA && "text-[#555555]")} />
          {!collapsed && <span>Settings</span>}
        </Link>

        {isMA && !collapsed && (
          <button onClick={() => navigate('/lumina/documentation')}
            className="w-full flex flex-col items-start gap-0.5 rounded-lg text-white text-[12px] font-medium bg-[#6b5ce7] hover:bg-[#5a4dd6] transition-colors"
            style={{ padding: '10px 14px' }}
            data-testid="ma-lens-cta">
            <div className="flex items-center gap-2">
              <ScanLine className="h-4 w-4" />
              <span>Skip the manual entry</span>
            </div>
            <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.6)', marginLeft: 24 }}>Scan docs, IDs, schedules</span>
          </button>
        )}
      </div>

      <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)}
        className={cn(
          "absolute -right-3 top-8 h-6 w-6 rounded-full shadow-sm",
          isMA ? "bg-[#1a1a1a] border border-[#2a2a2a] text-[#aaaaaa] hover:text-white hover:bg-[#2a2a2a]"
                : "bg-white border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50"
        )}>
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </Button>
    </aside>
  );
};

export default LuminaSidebar;
